/**
 * EN-1517: keys, matching and ordering for the Download Sessions panel's cards
 * once they can come from three places: a placeholder created in this browser
 * when a download is requested, SignalR progress for a real DownloadSession,
 * and GET /download-sessions/recent (real sessions plus 'Q' rows for download
 * commands still queued for the device).
 *
 * Store keys stay numbers so the EN-1506 retry code (which uses
 * downloadSessionId > 0 for a real session) is unchanged:
 * - real session: its DownloadSessionId (> 0)
 * - placeholder: -1, -2, ... (from the store's counter)
 * - queued command ('Q'): -(QUEUED_KEY_OFFSET + commandId)
 */
import {
  DownloadSessionCard,
  IDownloadSessionSummary,
} from '../models/DownloadSessionCard';

export const QUEUED_KEY_OFFSET = 1_000_000_000;

/** A placeholder no queued command was found for becomes Unknown after this. */
export const PENDING_EXPIRY_MS = 2 * 60 * 1000;

/** Range tolerance when matching (SQL datetime rounds to about 3 ms). */
export const RANGE_TOLERANCE_MS = 1000;

/** Clock skew allowed between this browser and the server's CreatedDate. */
export const CREATED_SKEW_MS = 60 * 1000;

export const queuedCardKey = (commandId: number): number =>
  -(QUEUED_KEY_OFFSET + commandId);

export const isQueuedKey = (key: number): boolean => key <= -QUEUED_KEY_OFFSET;

export const isPlaceholderKey = (key: number): boolean =>
  key < 0 && key > -QUEUED_KEY_OFFSET;

/** The parts of a card that identify which download it is. */
export interface DownloadIdentity {
  uid: number;
  fileType: number | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
}

/**
 * A WebApi date-time: `yyyy-MM-dd HH:mm[:ss[.f{1,7}]]` with a space or 'T'
 * separator and an optional zone (Z or +hh:mm / -hhmm). The WebApi sends
 * DateTime values as `"2026-06-25 17:00:00.000000"`: UTC with no zone.
 */
const SERVER_DATE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;

/**
 * Milliseconds for a WebApi date, or NaN when empty, not in that shape, or
 * not a real date/time. A value with no zone is UTC (the WebApi stores UTC),
 * never browser-local time; a value with Z or an offset is read as given.
 * Parsed by hand so every browser reads the space-separated form the same
 * way. Anything else (other shapes, Feb 30, hour 25, offset +25:00) is NaN:
 * there is no fallback to Date.parse, which would read zone-less text as
 * browser-local time.
 */
export function parseServerDate(value: string | null | undefined): number {
  if (!value) return NaN;
  const m = SERVER_DATE.exec(value.trim());
  if (!m) return NaN;
  const [, ys, mos, ds, hs, mis, secs, frac, zone] = m;
  const [y, mo, d, h, mi, sec] = [ys, mos, ds, hs, mis, secs ?? '0'].map(
    Number
  );
  if (h > 23 || mi > 59 || sec > 59) return NaN;
  const ms = frac ? Number((frac + '00').substring(0, 3)) : 0;
  const utc = new Date(Date.UTC(y, mo - 1, d, h, mi, sec, ms));
  // Date.UTC rolls over (Feb 30 becomes Mar 2) and maps years 0-99 to 19xx.
  if (
    utc.getUTCFullYear() !== y ||
    utc.getUTCMonth() !== mo - 1 ||
    utc.getUTCDate() !== d
  ) {
    return NaN;
  }
  let t = utc.getTime();
  if (zone && zone.toUpperCase() !== 'Z') {
    const digits = zone.substring(1).replace(':', '');
    const oh = Number(digits.substring(0, 2));
    const om = Number(digits.substring(2));
    if (oh > 23 || om > 59) return NaN;
    t -= (zone[0] === '-' ? -1 : 1) * (oh * 60 + om) * 60000;
  }
  return t;
}

/** `yyyy-MM-dd HH:mm:ss UTC` for a WebApi date; '' when empty or unreadable. */
export function formatServerDate(value: string | null | undefined): string {
  const t = parseServerDate(value);
  if (Number.isNaN(t)) return '';
  return new Date(t).toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
}

/** The UTC day of a WebApi date as `yyyy/MM/dd` (the Quasar date format). */
export function serverDateUtcDay(value: string | null | undefined): string {
  const t = parseServerDate(value);
  if (Number.isNaN(t)) return '';
  return new Date(t).toISOString().substring(0, 10).replace(/-/g, '/');
}

const time = parseServerDate;

const closeTimes = (
  a: string | null | undefined,
  b: string | null | undefined
): boolean => {
  const ta = time(a);
  const tb = time(b);
  return (
    !Number.isNaN(ta) &&
    !Number.isNaN(tb) &&
    Math.abs(ta - tb) <= RANGE_TOLERANCE_MS
  );
};

/**
 * Same UID, file type (Iridium has none; treated as 0, like the progress
 * message) and range. A placeholder is matched by this, never by UID alone, so
 * two downloads for one UID keep their own cards.
 */
export function sameDownload(
  a: DownloadIdentity,
  b: DownloadIdentity
): boolean {
  return (
    a.uid === b.uid &&
    (a.fileType ?? 0) === (b.fileType ?? 0) &&
    closeTimes(a.startDate, b.startDate) &&
    closeTimes(a.endDate, b.endDate)
  );
}

/** Store key for a row of GET /download-sessions/recent. */
export const summaryKey = (row: IDownloadSessionSummary): number =>
  row.downloadSessionId != null && row.downloadSessionId > 0
    ? row.downloadSessionId
    : queuedCardKey(row.commandId);

const TERMINAL = new Set(['C', 'X', 'A']);
const ACTIVE = new Set(['T', 'D', 'R']);

export const isActiveState = (state: string): boolean => ACTIVE.has(state);
export const isTerminalState = (state: string): boolean => TERMINAL.has(state);

/** A card still waiting for the device: a 'Q' row or a live placeholder. */
export const isWaitingCard = (card: DownloadSessionCard): boolean =>
  card.state === 'Q' ||
  (isPlaceholderKey(card.downloadSessionId) && !card.unknown);

const serverCreatedAt = (row: IDownloadSessionSummary): number | undefined => {
  const t = time(row.createdDate ?? row.queuedAt);
  return Number.isNaN(t) ? undefined : t;
};

/**
 * Builds the card for a server row, keeping what only this browser knows
 * (passages, messages, EN-1506 retry flags while the state is the same). When
 * SignalR already moved a session to a final state the (slightly older)
 * server row does not move it back.
 */
export function cardFromSummary(
  row: IDownloadSessionSummary,
  existing?: DownloadSessionCard
): DownloadSessionCard {
  const key = summaryKey(row);
  if (
    existing &&
    isTerminalState(existing.state) &&
    !isTerminalState(row.state)
  ) {
    return {
      ...existing,
      // A card first seen through SignalR takes the server's date.
      createdAt: existing.createdAt ?? serverCreatedAt(row),
      commandId: row.commandId,
      isIridium: row.isIridium,
      cancellable: false,
    };
  }
  const sameState = existing?.state === row.state;
  return {
    downloadSessionId: key,
    uid: row.uid,
    fileType: row.fileType ?? 0,
    state: row.state,
    percentComplete: row.percentComplete ?? 0,
    latestPassageTimestamp: row.latestPassageTimestamp ?? '',
    // Null: the server skipped the count (it counts at most 20 sessions per
    // response), so keep what the card has.
    passageCount: row.passageCount ?? existing?.passageCount ?? 0,
    startDate: row.startDate ?? '',
    endDate: row.endDate ?? '',
    passages: existing?.passages ?? [],
    passagesLoadedState: existing?.passagesLoadedState,
    message: existing?.message,
    retryable: sameState ? existing?.retryable : undefined,
    retryError: sameState ? existing?.retryError : undefined,
    commandId: row.commandId,
    createdAt: existing?.createdAt ?? serverCreatedAt(row),
    isIridium: row.isIridium,
    cancellable: row.cancellable,
    cancelError: sameState ? existing?.cancelError : undefined,
    cancelMessage: sameState ? existing?.cancelMessage : undefined,
  };
}

/**
 * Whether a server row can be the download a placeholder was created for: a
 * queued command always can; a session only if it was created after the
 * placeholder (allowing for clock skew), so re-downloading a range never
 * matches the old session for that range.
 */
export function canFulfilPlaceholder(
  placeholder: DownloadSessionCard,
  row: IDownloadSessionSummary
): boolean {
  if (!sameDownload(placeholder, row)) return false;
  if (row.state === 'Q') return true;
  const created = time(row.createdDate);
  if (Number.isNaN(created) || placeholder.createdAt == null) return true;
  return created >= placeholder.createdAt - CREATED_SKEW_MS;
}

const rank = (card: DownloadSessionCard): number => {
  if (isWaitingCard(card)) return 0;
  if (isActiveState(card.state)) return 1;
  if (card.unknown) return 3;
  return 2;
};

const recency = (card: DownloadSessionCard): number => {
  if (card.downloadSessionId > 0) return card.downloadSessionId;
  return card.createdAt ?? 0;
};

/**
 * Panel order: waiting (queued and placeholders), then running, then
 * finished, then Unknown; newest first inside each group.
 */
export function sortCards(
  cards: readonly DownloadSessionCard[]
): DownloadSessionCard[] {
  return [...cards].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    if (a.downloadSessionId > 0 && b.downloadSessionId > 0)
      return b.downloadSessionId - a.downloadSessionId;
    return recency(b) - recency(a) || a.downloadSessionId - b.downloadSessionId;
  });
}

export interface DownloadSessionCounts {
  total: number;
  /** Queued for the device (Q rows and live placeholders). */
  queued: number;
  /** Running (T, D, R). */
  active: number;
  complete: number;
  timedOut: number;
  cancelled: number;
  unknown: number;
}

export function countCards(
  cards: readonly DownloadSessionCard[]
): DownloadSessionCounts {
  const counts: DownloadSessionCounts = {
    total: cards.length,
    queued: 0,
    active: 0,
    complete: 0,
    timedOut: 0,
    cancelled: 0,
    unknown: 0,
  };
  for (const c of cards) {
    if (c.unknown) counts.unknown++;
    else if (isWaitingCard(c)) counts.queued++;
    else if (isActiveState(c.state)) counts.active++;
    else if (c.state === 'C') counts.complete++;
    else if (c.state === 'X') counts.timedOut++;
    else if (c.state === 'A') counts.cancelled++;
  }
  return counts;
}

/**
 * EN-1517: the closed panel's counts button, e.g. "1 queued · 2 running ·
 * 3 finished". Finished counts complete, timed-out and cancelled cards.
 */
export function countsLabel(counts: DownloadSessionCounts): string {
  const finished = counts.complete + counts.timedOut + counts.cancelled;
  const parts: string[] = [];
  if (counts.queued > 0) parts.push(`${counts.queued} queued`);
  if (counts.active > 0) parts.push(`${counts.active} running`);
  if (finished > 0) parts.push(`${finished} finished`);
  if (counts.unknown > 0) parts.push(`${counts.unknown} unknown`);
  return parts.length > 0 ? parts.join(' · ') : 'Downloads';
}

/** The fields of a `GET /commands/queued` row (IQueuedCommand) the sync needs. */
export interface QueuedListRow {
  commandId: number;
  isDownload?: boolean;
  isStopRow?: boolean;
  downloadSessionId?: number | null;
  /** EN-1516: the queued range, edited in place (same commandId). */
  parameters?: {
    FileNamesStartDate?: string | null;
    FileNamesEndDate?: string | null;
  } | null;
}

/**
 * EN-1557: a key for the Download Manager's queued download commands (the
 * dialog list). It changes when a download is queued, cancelled, sent or its
 * range edited (EN-1516), so the Download Sessions cards can reload when the
 * list changes. Stop rows and other commands are left out; the order of the
 * rows does not matter.
 */
export function queuedListSignature(
  rows: readonly QueuedListRow[] | null | undefined
): string {
  return (rows ?? [])
    .filter((r) => r.isDownload === true && r.isStopRow !== true)
    .map(
      (r) =>
        `${r.commandId}${r.downloadSessionId != null ? ':s' : ''}` +
        `@${r.parameters?.FileNamesStartDate ?? ''}` +
        `/${r.parameters?.FileNamesEndDate ?? ''}`
    )
    .sort()
    .join(',');
}
